// Section 16: Workers first norm. Established by rationing workers ahead of dependants while a
// dependant went short (4 uses in a row; reset by equal shares or dependants ranked ahead); lost
// after 8 seasons without reinforcement. Effect once established: non-workers' trust penalties
// for short rations halve (they expect it), children's happiness -3 baseline.
// Constants: config.normRules, config.norms['workers-first'].
import type { Config } from '../../config';
import { mod } from '../../core/modifiers';
import {
  DEPENDANT_GROUPS,
  WORKER_GROUPS,
  foodPrecedent,
  ranksBefore,
  targetPerson,
  wentShort,
} from '../../core/content-helpers';
import { isChild } from '../../core/queries';
import { defineNorm } from '../../core/registry';

const SHORT_CAUSES: readonly string[] = ['rationed-short', 'rationed-short-deviation'];

export function workersFirst(config: Config) {
  const c = config.norms['workers-first'];

  return defineNorm({
    id: 'workers-first',
    label: 'Workers first',
    section: 'S16',
    establishAfter: config.normRules.establishAfter,
    decayAfter: config.normRules.decayAfter,
    reinforcedBy(ctx, entry) {
      const p = foodPrecedent(ctx, entry);
      return (
        p !== null &&
        p.policy !== 'equal' &&
        ranksBefore(p.order, WORKER_GROUPS, DEPENDANT_GROUPS) &&
        wentShort(p, DEPENDANT_GROUPS, true)
      );
    },
    contradictedBy(ctx, entry) {
      const p = foodPrecedent(ctx, entry);
      return (
        p !== null &&
        (p.policy === 'equal' || ranksBefore(p.order, DEPENDANT_GROUPS, WORKER_GROUPS))
      );
    },
    modifierTargets: ['trustChange', 'happinessTarget'],
    modifierKeys: { trustChange: SHORT_CAUSES },
    modifiers(ctx, target) {
      if (!ctx.state.norms['workers-first'].established) return [];
      const p = targetPerson(ctx, target);
      if (p === null) return [];
      if (target.kind === 'trustChange' && SHORT_CAUSES.includes(target.cause) && p.job === 'rest')
        return [mod.mul('norm:workers-first', c.nonWorkerTrustMul, 'expected under workers first')];
      if (target.kind === 'happinessTarget' && isChild(ctx.cfg, p))
        return [mod.add('norm:workers-first', c.childHappiness, 'workers first')];
      return [];
    },
  });
}
